// @flow

/**
 * Scroll Orchestrator Hook
 * Centralized scroll orchestration system with priority-based coordination
 * Manages all scrolling behaviors for the chat component
 */

import { useEffect, useRef, useCallback } from 'react';
import {
  SCROLL_MODE,
  SCROLL_PRIORITY,
  DEFAULT_AUTO_SCROLL_THRESHOLD_PX,
  DEFAULT_SCROLL_DEBOUNCE_MS,
} from '../constants';
import { isAtBottom, scrollToElement, scrollToBottom } from '../services/scrollService';

/**
 * Scroll orchestrator hook
 * @param {Array} messages - Chat messages array
 * @param {boolean} isStreaming - Whether a message is currently streaming
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Threshold in pixels to consider "at bottom"
 * @param {number} options.debounceMs - Debounce delay for scroll operations
 * @param {Function} options.onModeChange - Callback when scroll mode changes
 * @returns {Object} Scroll service API
 */
const useScrollOrchestrator = (
  messages,
  isStreaming,
  options = {}
) => {
  const {
    threshold = DEFAULT_AUTO_SCROLL_THRESHOLD_PX,
    debounceMs = DEFAULT_SCROLL_DEBOUNCE_MS,
    onModeChange,
  } = options;

  // Refs
  const containerRef = useRef(null);
  const endRef = useRef(null);
  const scrollModeRef = useRef(SCROLL_MODE.AUTO);
  const debounceTimeoutRef = useRef(null);
  const pauseTimeoutRef = useRef(null);
  const prevMessageCountRef = useRef(messages.length);

  /**
   * Set scroll mode and trigger callback if provided
   */
  const setScrollMode = useCallback((newMode) => {
    if (scrollModeRef.current !== newMode) {
      scrollModeRef.current = newMode;
      if (onModeChange) {
        onModeChange(newMode);
      }
    }
  }, [onModeChange]);

  /**
   * Get current scroll mode
   */
  const getMode = useCallback(() => {
    return scrollModeRef.current;
  }, []);

  /**
   * Request a scroll operation with priority
   * @param {HTMLElement} element - Element to scroll into view
   * @param {Object} options - Scroll options
   * @param {string} options.priority - 'low', 'medium', or 'high'
   * @param {string} options.behavior - 'auto' or 'smooth'
   * @param {string} options.block - 'start', 'center', 'end', or 'nearest'
   */
  const requestScroll = useCallback((element, scrollOptions = {}) => {
    if (!element) return;

    const {
      priority = 'low',
      behavior = 'smooth',
      block = 'end',
      inline = 'nearest',
    } = scrollOptions;

    // Map priority string to number
    const priorityMap = {
      low: SCROLL_PRIORITY.LOW,
      medium: SCROLL_PRIORITY.MEDIUM,
      high: SCROLL_PRIORITY.HIGH,
    };
    const priorityLevel = priorityMap[priority] || SCROLL_PRIORITY.LOW;

    // Check current mode and priority
    const currentMode = scrollModeRef.current;

    // HIGH priority (manual) always takes control
    if (priorityLevel === SCROLL_PRIORITY.HIGH) {
      setScrollMode(SCROLL_MODE.MANUAL);
      scrollToElement(element, { behavior, block, inline });
      return;
    }

    // MEDIUM priority can interrupt AUTO but not MANUAL
    if (priorityLevel === SCROLL_PRIORITY.MEDIUM) {
      if (currentMode !== SCROLL_MODE.MANUAL) {
        setScrollMode(SCROLL_MODE.PAUSED);
        scrollToElement(element, { behavior, block, inline });
        
        // Auto-resume after a delay
        if (pauseTimeoutRef.current) {
          clearTimeout(pauseTimeoutRef.current);
        }
        pauseTimeoutRef.current = setTimeout(() => {
          if (scrollModeRef.current === SCROLL_MODE.PAUSED) {
            setScrollMode(SCROLL_MODE.AUTO);
          }
        }, 1000);
      }
      return;
    }

    // LOW priority (auto-scroll) only works in AUTO mode
    if (priorityLevel === SCROLL_PRIORITY.LOW && currentMode === SCROLL_MODE.AUTO) {
      scrollToElement(element, { behavior, block, inline });
    }
  }, [setScrollMode]);

  /**
   * Pause auto-scroll for a specified duration
   * @param {number} durationMs - Duration in milliseconds to pause
   */
  const pauseAutoScroll = useCallback((durationMs = 1000) => {
    if (scrollModeRef.current === SCROLL_MODE.AUTO) {
      setScrollMode(SCROLL_MODE.PAUSED);
      
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
      
      pauseTimeoutRef.current = setTimeout(() => {
        if (scrollModeRef.current === SCROLL_MODE.PAUSED) {
          setScrollMode(SCROLL_MODE.AUTO);
        }
      }, durationMs);
    }
  }, [setScrollMode]);

  /**
   * Resume auto-scroll (switch from PAUSED or MANUAL to AUTO)
   */
  const resumeAutoScroll = useCallback(() => {
    setScrollMode(SCROLL_MODE.AUTO);
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
    }
  }, [setScrollMode]);

  /**
   * Handle user scroll event
   * Detects when user scrolls and updates mode accordingly
   */
  const handleUserScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    // Clear any pending debounce
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Debounce the scroll check
    debounceTimeoutRef.current = setTimeout(() => {
      const atBottom = isAtBottom(container, threshold);
      
      if (atBottom) {
        // User scrolled back to bottom, resume auto-scroll
        if (scrollModeRef.current === SCROLL_MODE.MANUAL) {
          setScrollMode(SCROLL_MODE.AUTO);
        }
      } else {
        // User scrolled away from bottom, disable auto-scroll
        if (scrollModeRef.current === SCROLL_MODE.AUTO) {
          setScrollMode(SCROLL_MODE.MANUAL);
        }
      }
    }, debounceMs);
  }, [threshold, debounceMs, setScrollMode]);

  /**
   * Auto-scroll effect
   * Triggers when messages change or streaming state changes
   */
  useEffect(() => {
    // Resume auto-scroll when new messages are added
    // This ensures user sees new content even if they had scrolled up
    if (messages.length > prevMessageCountRef.current) {
      if (scrollModeRef.current === SCROLL_MODE.MANUAL) {
        setScrollMode(SCROLL_MODE.AUTO);
      }
    }
    prevMessageCountRef.current = messages.length;

    if (scrollModeRef.current === SCROLL_MODE.AUTO && endRef.current) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        if (endRef.current && scrollModeRef.current === SCROLL_MODE.AUTO) {
          endRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
        }
      });
    }
  }, [messages, isStreaming, setScrollMode]);

  /**
   * Cleanup effect
   */
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
    };
  }, []);

  // Return API
  return {
    containerRef,
    endRef,
    handleScroll: handleUserScroll,
    requestScroll,
    pauseAutoScroll,
    resumeAutoScroll,
    getMode,
  };
};

export default useScrollOrchestrator;
